/**
 * Airport + railway-station directory for the Local (station / airport transfer)
 * product, scoped to the metro cities Pulpit serves.
 *
 * Data is real and verifiable (airport IATA codes + coordinates + terminals,
 * major railway station names + codes). Scoped to served metros rather than an
 * exhaustive all-India list — extend SERVED_CITIES to add more, or wire an
 * authoritative dataset (AAI airports / Indian Railways stations) later.
 *
 * Airports carry real lat/lng (they sit well outside the city centre, so their
 * own coordinates are needed for distance-based fares). Stations fall back to
 * the city-centre lat/lng (they are central). City lat/lng also feeds the
 * booking record; the airport fare is computed from the customer location to
 * the hub — see src/utils/tempRates.js.
 */

export const SERVED_CITIES = [
  {
    key: "delhi", name: "Delhi", lat: 28.6139, lng: 77.209,
    airport: { code: "DEL", name: "Indira Gandhi International Airport", lat: 28.5562, lng: 77.1, terminals: ["Terminal 1", "Terminal 2", "Terminal 3"] },
    stations: [
      { code: "NDLS", name: "New Delhi" },
      { code: "DLI", name: "Delhi Junction (Old Delhi)" },
      { code: "NZM", name: "Hazrat Nizamuddin" },
      { code: "ANVT", name: "Anand Vihar Terminal" },
      { code: "DEE", name: "Delhi Sarai Rohilla" },
    ],
  },
  {
    key: "mumbai", name: "Mumbai", lat: 19.076, lng: 72.8777,
    airport: { code: "BOM", name: "Chhatrapati Shivaji Maharaj International Airport", lat: 19.0896, lng: 72.8656, terminals: ["Terminal 1", "Terminal 2"] },
    stations: [
      { code: "CSMT", name: "Chhatrapati Shivaji Maharaj Terminus" },
      { code: "MMCT", name: "Mumbai Central" },
      { code: "DR", name: "Dadar" },
      { code: "LTT", name: "Lokmanya Tilak Terminus (Kurla)" },
      { code: "BDTS", name: "Bandra Terminus" },
    ],
  },
  {
    key: "bengaluru", name: "Bengaluru", lat: 12.9716, lng: 77.5946,
    airport: { code: "BLR", name: "Kempegowda International Airport", lat: 13.1979, lng: 77.7063, terminals: ["Terminal 1", "Terminal 2"] },
    stations: [
      { code: "SBC", name: "KSR Bengaluru City Junction" },
      { code: "YPR", name: "Yesvantpur Junction" },
      { code: "SMVB", name: "Sir M Visvesvaraya Terminal (Baiyappanahalli)" },
      { code: "BNC", name: "Bengaluru Cantonment" },
    ],
  },
  {
    key: "hyderabad", name: "Hyderabad", lat: 17.385, lng: 78.4867,
    airport: { code: "HYD", name: "Rajiv Gandhi International Airport", lat: 17.2403, lng: 78.4294, terminals: ["Main Terminal"] },
    stations: [
      { code: "SC", name: "Secunderabad Junction" },
      { code: "HYB", name: "Hyderabad Deccan (Nampally)" },
      { code: "KCG", name: "Kacheguda" },
    ],
  },
  {
    key: "chennai", name: "Chennai", lat: 13.0827, lng: 80.2707,
    airport: { code: "MAA", name: "Chennai International Airport", lat: 12.99, lng: 80.1693, terminals: ["Domestic Terminal", "International Terminal"] },
    stations: [
      { code: "MAS", name: "MGR Chennai Central" },
      { code: "MS", name: "Chennai Egmore" },
      { code: "TBM", name: "Tambaram" },
    ],
  },
  {
    key: "kolkata", name: "Kolkata", lat: 22.5726, lng: 88.3639,
    airport: { code: "CCU", name: "Netaji Subhas Chandra Bose International Airport", lat: 22.652, lng: 88.4463, terminals: ["Main Terminal"] },
    stations: [
      { code: "HWH", name: "Howrah Junction" },
      { code: "SDAH", name: "Sealdah" },
      { code: "KOAA", name: "Kolkata (Chitpur)" },
      { code: "SRC", name: "Santragachi Junction" },
    ],
  },
  {
    key: "ahmedabad", name: "Ahmedabad", lat: 23.0225, lng: 72.5714,
    airport: { code: "AMD", name: "Sardar Vallabhbhai Patel International Airport", lat: 23.0725, lng: 72.6347, terminals: ["Terminal 1 (Domestic)", "Terminal 2 (International)"] },
    stations: [
      { code: "ADI", name: "Ahmedabad Junction (Kalupur)" },
      { code: "MAN", name: "Maninagar" },
      { code: "SBIB", name: "Sabarmati" },
    ],
  },
  {
    key: "pune", name: "Pune", lat: 18.5204, lng: 73.8567,
    airport: { code: "PNQ", name: "Pune Airport (Lohegaon)", lat: 18.5822, lng: 73.9197, terminals: ["Main Terminal"] },
    stations: [
      { code: "PUNE", name: "Pune Junction" },
      { code: "SVJR", name: "Shivajinagar" },
      { code: "HDP", name: "Hadapsar" },
    ],
  },
  {
    key: "surat", name: "Surat", lat: 21.1702, lng: 72.8311,
    airport: { code: "STV", name: "Surat Airport", lat: 21.1141, lng: 72.7418, terminals: ["Main Terminal"] },
    stations: [
      { code: "ST", name: "Surat" },
      { code: "UDN", name: "Udhna Junction" },
    ],
  },
  {
    key: "jaipur", name: "Jaipur", lat: 26.9124, lng: 75.7873,
    airport: { code: "JAI", name: "Jaipur International Airport", lat: 26.8242, lng: 75.8103, terminals: ["Terminal 1", "Terminal 2"] },
    stations: [
      { code: "JP", name: "Jaipur Junction" },
      { code: "GADJ", name: "Gandhinagar Jaipur" },
      { code: "DPA", name: "Durgapura" },
    ],
  },
]

/**
 * Grouped react-select-style options for the hub picker.
 * mode: "airport" | "railway". Grouped by city; each option carries the data
 * (city, coords, display name) needed to build the trip payload + distance fare.
 * Airport options use the airport's own coordinates; stations use city centre.
 */
export const buildHubOptions = (mode) =>
  SERVED_CITIES.map((city) => {
    const options =
      mode === "airport"
        ? city.airport.terminals.map((t) => ({
            label: `${t} (${city.airport.code})`,
            value: `${city.key}|air|${city.airport.code}|${t}`,
            hub: {
              kind: "airport",
              cityKey: city.key,
              cityName: city.name,
              lat: city.airport.lat,
              lng: city.airport.lng,
              name: `${city.name} Airport, ${t}`,
              fullAddress: `${city.airport.name}, ${t}, ${city.name}`,
            },
          }))
        : city.stations.map((s) => ({
            label: `${s.name} (${s.code})`,
            value: `${city.key}|rail|${s.code}`,
            hub: {
              kind: "railway",
              cityKey: city.key,
              cityName: city.name,
              lat: city.lat,
              lng: city.lng,
              name: `${s.name} Railway Station`,
              fullAddress: `${s.name} Railway Station, ${city.name}`,
            },
          }))
    return { label: city.name, options }
  })
